import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { UserStatusReportSelectionSchema } from "@forgepoint/shared";
import { StatusReportSelectItems } from "@/components/settings/statusReports/StatusReportSelectItems";
import { useAuthStore } from "@/store/authStore";
import { useStatusReportTemplatesStore } from "@/store/statusReportTemplatesStore";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * The personal "Status Report" picker: which daily report the user receives
 * (or Off). Saves through the auth store's self-scoped PUT /users/me; report
 * content stays principal-scoped. Only templates the org has enabled are
 * offered, but a current selection the org later disabled stays visible.
 */
export function StatusReportPickerRow() {
  const user = useAuthStore((state) => state.user);
  const updateSelection = useAuthStore(
    (state) => state.updateStatusReportSelection,
  );
  const enabledTemplateKeys = useStatusReportTemplatesStore(
    (state) => state.enabledTemplateKeys,
  );
  const fetchEnabledTemplateKeys = useStatusReportTemplatesStore(
    (state) => state.fetchEnabledTemplateKeys,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchEnabledTemplateKeys();
  }, [fetchEnabledTemplateKeys]);

  const handleChange = async (value: string) => {
    const parsed = UserStatusReportSelectionSchema.safeParse(value);
    if (!parsed.success) {
      return;
    }
    setIsSaving(true);
    try {
      await updateSelection(parsed.data);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsRow
      id="personal-status-report"
      label="Daily report"
      description="Which daily status report you receive from your agent. Content is always scoped to what you can see."
    >
      <div className="flex items-center gap-2">
        {isSaving && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        <Select
          value={user?.statusReportTemplate}
          onValueChange={handleChange}
          disabled={isSaving}
        >
          <SelectTrigger className="w-[240px]" aria-label="Daily report">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <StatusReportSelectItems
              enabledTemplateKeys={enabledTemplateKeys}
              currentSelection={user?.statusReportTemplate}
            />
          </SelectContent>
        </Select>
      </div>
    </SettingsRow>
  );
}
